import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  fetchDeliveries,
  markDeliveryAsActioned,
  addActionNote,
  getStatusCounts,
  getResultField,
  setSoNumber as setSoNumberService,
  REVIEW_OPERATIONS,
} from '../services/deliveryService';
import { useAuth } from './AuthContext';
import { getRoleConfig } from '../config/roles';

const DeliveryContext = createContext(null);

export function DeliveryProvider({ children }) {
  const { user } = useAuth();
  const roleConfig = getRoleConfig(user?.role);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [operationType, setOperationType] = useState(roleConfig.defaultOperation);
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    needsAction: 0,
    completed: 0,
    actioned: 0,
  });

  const lastFiltersRef = useRef({});

  /**
   * Split deliveries into needsAction and completed sections.
   * - Review operations (Exchange/Pickup):
   *     needs action = NOT actioned AND (sapResult null OR sapResult === 'Failed')
   *     completed    = actioned OR sapResult === 'Success'
   *   (Legacy rows have sapResult null and behave like the original manual flow.)
   * - Other operations: needs action = Failed AND not actioned; completed = otherwise
   */
  const splitDeliveries = useCallback((allDeliveries, operation) => {
    if (REVIEW_OPERATIONS.has(operation)) {
      // Pickup/Exchange: hybrid legacy + auto-SO logic.
      // - sapResult null      => legacy / pending: needs operator until actioned
      // - sapResult 'Success' => auto-SO succeeded: counted as completed
      // - sapResult 'Failed'  => auto-SO failed: needs operator until actioned
      const needsAction = allDeliveries.filter(
        (d) => !d.isActioned && (d.sapResult == null || d.sapResult === 'Failed')
      );
      const completed = allDeliveries.filter(
        (d) => d.isActioned || d.sapResult === 'Success'
      );
      return { needsAction, completed };
    }

    const resultField = operation === 'SAP_SO' ? 'sapResult' : 'storisResult';
    const needsAction = allDeliveries.filter(
      (d) => d[resultField] === 'Failed' && !d.isActioned
    );
    const completed = allDeliveries.filter(
      (d) => d[resultField] !== 'Failed' || d.isActioned
    );
    return { needsAction, completed };
  }, []);

  const loadDeliveries = useCallback(async (filters) => {
    if (filters) {
      lastFiltersRef.current = filters;
    }
    const filtersToUse = filters || lastFiltersRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchDeliveries(filtersToUse);
      setDeliveries(data);
      const counts = await getStatusCounts(filtersToUse.operation, filtersToUse.daysBack);
      setStatusCounts(counts);
    } catch (err) {
      console.error('Error loading deliveries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeOperationType = useCallback((op) => {
    setOperationType(op);
  }, []);

  const markAsActioned = useCallback(async (delivery) => {
    setError(null);

    try {
      await markDeliveryAsActioned(delivery.id, user?.email);
      await loadDeliveries();
    } catch (err) {
      console.error('Error marking delivery as actioned:', err);
      setError(err.message);
      throw err;
    }
  }, [user, loadDeliveries]);

  const addNote = useCallback(async (deliveryId, noteText) => {
    setError(null);

    try {
      await addActionNote(deliveryId, noteText, user?.email);
      await loadDeliveries();
    } catch (err) {
      console.error('Error adding note:', err);
      setError(err.message);
      throw err;
    }
  }, [user, loadDeliveries]);

  const submitSoNumber = useCallback(async (delivery, numbers) => {
    setError(null);

    try {
      await setSoNumberService(delivery, numbers);
      const noteParts = [];
      if (numbers.sapSoNumber) noteParts.push(`SAP SO: ${numbers.sapSoNumber}`);
      if (numbers.pickupSapSoNumber) noteParts.push(`Pickup SAP SO: ${numbers.pickupSapSoNumber}`);
      await addActionNote(delivery.id, `Manually set ${noteParts.join(', ')}`, user?.email);
      await loadDeliveries();
    } catch (err) {
      console.error('Error setting SO number:', err);
      setError(err.message);
      throw err;
    }
  }, [user, loadDeliveries]);

  const value = {
    deliveries,
    loading,
    error,
    statusCounts,
    operationType,
    loadDeliveries,
    changeOperationType,
    markAsActioned,
    addNote,
    submitSoNumber,
    splitDeliveries,
  };

  return (
    <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>
  );
}

export function useDeliveries() {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error('useDeliveries must be used within a DeliveryProvider');
  }
  return context;
}

export default DeliveryContext;
